import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import { useStore } from "src/Shared/Hooks";
import CompanyService from "../../Services/Company.service";
import { IRouteParams } from "../../Interfaces";
import { IOnboardingSopDocument } from "../../Interfaces/IOnboardingOutputs";
import { copyToClipboard, downloadJsonFile, replaceDateTimePlaceholders } from "../../Tools/OnboardingOutputs";

/**
 * The setup copy view (S7.5, story 5631).
 *
 * The same values as the SOP, in the same section order — it reads the same server-built document —
 * with a copy control on every field. A copy puts the **raw stored value** on the clipboard, never
 * the label or any surrounding formatting, so an address, a code or a contact detail pastes straight
 * into Imperans. A clipboard failure is reported rather than failing silently.
 *
 * This is per field, and distinct from the SOP's Copy Email Text, which copies one covering note.
 */
const OnboardingSetupCopyView: React.FC = () => {
  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const [document, setDocument] = useState<IOnboardingSopDocument | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const {
    rootStore: {
      companyStore: { setOpenClientContext },
    },
  } = useStore();
  const [copied, setCopied] = useState<string>("");

  useEffect(() => {
    if (!companyRowID) return;
    CompanyService.getOnboardingSopDocument(companyRowID).subscribe({
      next: (data) => {
        setDocument(data);
        setLoading(false);
        // Client-scoped route with no client record behind it: publish the open client so the app
        // bar lights PROSPECTS or HOME correctly however this page was reached (bug 5688).
        if (data && companyRowID) {
          setOpenClientContext({
            companyRowID,
            clientStatusID: data.clientStatusID,
          });
        }
      },
      error: (error: AxiosError) => {
        setLoading(false);
        ToastService.axiosError(error);
      },
    });
  }, [companyRowID]);

  const copy = async (value: string, what: string) => {
    const result = await copyToClipboard(value, what);
    if (result.ok) {
      setCopied(result.message);
      setTimeout(() => setCopied(""), 2500);
    } else {
      ToastService.error(result.message);
    }
  };

  const onDownloadJson = () => {
    CompanyService.getOnboardingExport(companyRowID).subscribe({
      next: (data) => downloadJsonFile(data.fileName, data.json),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!document) {
    return <Alert severity="error">This client's setup view could not be loaded.</Alert>;
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <Typography variant="h5" sx={{ mr: 1 }}>
          Setup copy view
        </Typography>
        <Button component={Link} to={`/company/${companyRowID}/onboarding/sop`}>
          SOP output
        </Button>
        <Button onClick={onDownloadJson}>Download JSON</Button>
        <Button component={Link} to={`/company/${companyRowID}/overview`}>
          Back to client record
        </Button>
        {copied && <Chip size="small" color="success" label={copied} />}
      </Box>

      {document.isDraft && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This onboarding has not been submitted — these are draft values.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {document.companyName} · {document.reference} — every field copies its stored value on its own.
      </Typography>

      {document.blocks
        .filter((block) => block.key !== "header" && block.key !== "footer")
        .map((block) => (
          <Paper key={block.key} elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {block.title}
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {block.paragraph && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {block.paragraph}
              </Typography>
            )}

            {block.lines.map((line, index) =>
              line.isHeading ? (
                <Typography
                  key={`${line.label}-${index}`}
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mt: 1 }}
                >
                  {line.label}
                </Typography>
              ) : (
                <Box
                  key={`${line.label}-${index}`}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    py: 0.25,
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220 }}>
                    {line.label}
                  </Typography>
                  <Typography variant="body2" sx={{ flexGrow: 1, whiteSpace: "pre-line" }}>
                    {replaceDateTimePlaceholders(line.value)}
                    {/* The captions carry the markings that matter most on the screen the setup
                        team pastes from — "TDDA staff only" beneath OCCF notes, and whether a point
                        of contact also receives results (bug 5720). */}
                    {line.caption && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {line.caption}
                      </Typography>
                    )}
                    {line.tbc && (
                      <Chip size="small" color="warning" variant="outlined" label="TBC" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Tooltip title={`Copy ${line.label}`}>
                    <IconButton size="small" onClick={() => copy(replaceDateTimePlaceholders(line.value), line.label)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            )}

            {block.tables.map((table, index) => (
              <Box key={`${table.title}-${index}`} sx={{ mt: 1 }}>
                {table.title && (
                  <Typography variant="subtitle2" fontWeight={600}>
                    {table.title}
                  </Typography>
                )}
                {table.rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {table.emptyLine || "Not provided."}
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {table.headers.map((header) => (
                          <TableCell key={header} sx={{ fontWeight: 600 }}>
                            {header}
                          </TableCell>
                        ))}
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {table.rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <TableCell
                              key={cellIndex}
                              sx={{ whiteSpace: "pre-line", verticalAlign: "top" }}
                            >
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                <span>{cell}</span>
                                <Tooltip title={`Copy ${table.headers[cellIndex] || "value"}`}>
                                  <IconButton
                                    size="small"
                                    onClick={() => copy(cell, table.headers[cellIndex] || "Value")}
                                  >
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          ))}
                          {/* Copies the whole row, tab-separated, for a paste into a grid. */}
                          <TableCell>
                            <Tooltip title="Copy the whole row">
                              <IconButton
                                size="small"
                                onClick={() => copy(row.join("\t"), "Row")}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            ))}

            {!block.paragraph && !block.lines.length && !block.tables.length && block.emptyLine && (
              <Typography variant="body2" color="text.secondary">
                {block.emptyLine}
              </Typography>
            )}
          </Paper>
        ))}
    </>
  );
};

export default OnboardingSetupCopyView;
